/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Input,
  InputNumber,
  Select,
  Table,
  TablePaginationConfig,
  Tooltip,
} from 'antd';
import CurrencyInput from 'components/UI/CurrencyInputNew';
import Monetary from 'components/UI/Data/Monetary';
import * as BuyIntentionModels from 'model/BuyIntention';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import * as BuyIntentionSelectors from '../../../store/state/buyIntention/buyIntenteionSelectors';
import { actions as buyIntentionActions } from '../../../store/state/buyIntention/buyIntentionSlice';
import BuyIntentionItemModals, {
  BuyIntentionItemsLoading,
} from './BuyIntentionItemModals';
import { useBuyIntentionItemActions } from './useBuyIntentionItemActions';
import * as S from './styles';

interface Props {
  tableData?: any;
}

const { Option } = Select;

function CurrencyValue(value: number) {
  if (Number.isNaN(value)) return '-';

  const formattedShort = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 2,
  }).format(value);

  const formattedLong = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 6,
  }).format(value);

  return (
    <span className="currency-hover-value" title={formattedLong}>
      {formattedShort}
    </span>
  );
}

export default function AddBuyIntentionTable({ tableData }: Props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { PageSize, PageNumber } = useSelector(
    BuyIntentionSelectors.getPagination,
  );
  const actions = useBuyIntentionItemActions();
  const {
    currentStep,
    customer,
    isLoading,
    changeOfferInput,
    choicePacking,
    choiceProducer,
    changeQuantityPacking,
    stepQuantityPacking,
    changeQuantityUnit,
    changeUnitPrice,
    searchOffers,
    handleTotalPrice,
    openObservation,
    requestDelete,
  } = actions;

  const handlePageChange = useCallback(
    (pagination: TablePaginationConfig) => {
      dispatch(
        buyIntentionActions.setPagination({
          PageSize: pagination.pageSize ?? 10,
          PageNumber: pagination.current ?? 1,
        }),
      );
    },
    [dispatch],
  );

  const validatorAlign = (isDisabled: boolean, isValue: boolean) => {
    if (isDisabled) {
      return 0;
    }
    if (isValue && !isDisabled) {
      return 0;
    }
    return 9;
  };

  function getPopupContainer(triggerNode: any) {
    return triggerNode.parentElement;
  }

  return (
    <>
      <BuyIntentionItemModals actions={actions} />

      <Table
        bordered
        dataSource={tableData}
        pagination={{
          current: PageNumber,
          pageSize: PageSize,
          showSizeChanger: true,
        }}
        onChange={(pagination) => handlePageChange(pagination)}
        rowKey="id"
      >
        <Table.Column
          key="productName"
          dataIndex="productName"
          title={t('buyIntention.table.productDescription')}
          render={(value, record: any) => (
            <S.GroupTitleInformation descroption>
              <Tooltip title={value}>
                <S.TitleTextWrap>{value}</S.TitleTextWrap>
              </Tooltip>
              {record.site?.conceptName && (
                <Tooltip title={record.site.conceptName}>
                  <S.ConceptText>{record.site.conceptName}</S.ConceptText>
                </Tooltip>
              )}
              <S.DescriptionText>{record.productCode}</S.DescriptionText>
              <S.BoughtContent>
                  {record.bought && (
                    <S.BoughtText>
                      {t('buyIntention.table.bought')}
                    </S.BoughtText>
                  )}
                  {record.intention && currentStep === 0 && (
                    <S.BoughtText>
                      {t('buyIntention.table.intention')}
                    </S.BoughtText>
                  )}
                </S.BoughtContent>
            </S.GroupTitleInformation>
          )}
        />

        <Table.Column
          width="25%"
          key="site"
          dataIndex="site"
          title={t('buyIntention.table.producer')}
          render={(value, record: BuyIntentionModels.BuyIntentionData) => (
            <Select
              dropdownStyle={{ maxHeight: 200, overflow: 'auto' }}
              placement="bottomLeft"
              getPopupContainer={(v) => getPopupContainer(v)}
              disabled={record.siteList === null}
              value={
                record.site !== null
                  ? `${record.site?.account} - ${record.site?.tradingName}`
                  : undefined
              }
              style={{ width: 250 }}
              allowClear
              showSearch
              optionFilterProp="children"
              filterOption
              placeholder={
                record.siteList === null
                  ? `${record.site?.account} - ${record.site?.tradingName}`
                  : 'Selecione'
              }
              onChange={(v) => choiceProducer(Number(v), record)}
            >
              {record.siteList !== undefined &&
                record.siteList !== null &&
                Array.isArray(record.siteList) &&
                record.siteList.map((item: any) => (
                  <Option key={item.id} value={item.id}>
                    {`${item.account} - ${item.tradingName}`}
                  </Option>
                ))}
            </Select>
          )}
        />

        <Table.Column
          key="quality"
          dataIndex="quality"
          title={t('buyIntention.table.quality')}
          render={(value, record: any) => (
            <Select
              getPopupContainer={(v) => getPopupContainer(v)}
              disabled={!record.site}
              style={{ width: 70 }}
              value={record.qualitySelect}
              allowClear={false}
              onChange={(v) => changeOfferInput(v, 'qualitySelect', record)}
            >
              {record.qualityList.map((item: any) => (
                <Option key={item.id} value={item.code}>
                  {item.description}
                </Option>
              ))}
            </Select>
          )}
        />

        <Table.Column
          dataIndex="productPreference"
          key="productPreferences"
          title={t('buyIntention.table.packing')}
          render={(
            productPreference: BuyIntentionModels.PackingFull[],
            record: BuyIntentionModels.BuyIntentionData,
          ) => (
            <>
              <Select
                getPopupContainer={(v) => getPopupContainer(v)}
                disabled={!productPreference}
                style={{
                  display: 'flex',
                  width: 80,
                  marginTop: validatorAlign(
                    !productPreference,
                    record.packingSelected?.additionalCost !== 'S',
                  ),
                }}
                bordered
                onChange={(v) => choicePacking(v, record)}
                value={record.packingSelected?.code || ''}
                allowClear={false}
                showSearch
                optionFilterProp="children"
                filterOption
                dropdownStyle={{ minWidth: '200px' }}
              >
                {productPreference &&
                  productPreference.map(
                    (packing: BuyIntentionModels.Packing) => (
                      <Option value={packing.code} key={packing.id}>
                        {packing.code} - {packing.abbrev}
                      </Option>
                    ),
                  )}
              </Select>
              {record.packingSelected?.additionalCost === 'S' && (
                <S.SubTextBody>
                  <Monetary symbol value={record.packingSelected.packingCost} />
                  /un.
                </S.SubTextBody>
              )}
            </>
          )}
        />

        <Table.Column
          width="10%"
          dataIndex="quantityPacking"
          key="quantityPacking"
          title={
            <S.CenterContent>{t('buyIntention.table.qe')}</S.CenterContent>
          }
          render={(value, _record: BuyIntentionModels.BuyIntentionData) => (
            <>
              <InputNumber
                style={{
                  width: 70,
                  marginTop: !_record.packingSelected ? 0 : 10,
                }}
                disabled={!_record.packingSelected}
                inputMode="numeric"
                defaultValue={_record.packingSelected?.qe}
                value={value}
                min={1}
                step={_record.packingSelected?.qe || 1}
                onStep={(value) => stepQuantityPacking(value, _record)}
                onChange={(value_: any) =>
                  changeQuantityPacking(value_, _record)
                }
              />
              {_record.packingSelected && (
                <S.SubTextBody>
                  {`Múltiplo ${_record.packingSelected?.qe || ''}`}
                </S.SubTextBody>
              )}
            </>
          )}
        />

        <Table.Column
          width="10%"
          dataIndex="quantityUnit"
          key="quantityUnit"
          title={
            <S.CenterContent>{t('buyIntention.table.qpe')}</S.CenterContent>
          }
          render={(value, _record: BuyIntentionModels.BuyIntentionData) => (
            <InputNumber
              style={{ width: 70 }}
              disabled={!_record.packingSelected}
              defaultValue={_record.packingSelected?.qpe}
              value={value}
              min={1}
              step={1}
              type="number"
              onChange={(value_: any) => changeQuantityUnit(value_, _record)}
            />
          )}
        />

        <Table.Column
          width="10%"
          dataIndex="unitPrice"
          key="unitPrice"
          title={
            <S.GroupTitleInformation>
              <S.TitleText>{t('buyIntention.table.unitValue')}</S.TitleText>
              <S.SubText>{t('buyIntention.table.noICMS')}</S.SubText>
            </S.GroupTitleInformation>
          }
          render={(
            priceByPacking,
            _record: BuyIntentionModels.BuyIntentionData,
          ) => (
            <CurrencyInput
              disabledOption={!_record.packingSelected}
              id={_record.id}
              value={priceByPacking}
              onChange={(e) => changeUnitPrice(e, _record)}
            />
          )}
        />

        {customer?.branch === 'CVH_UB' && (
          <Table.Column
            width="10%"
            dataIndex="shippingFeeFilials"
            key="shippingFeeFilials"
            title={
              <S.GroupTitleInformation>
                <S.TitleText>{t('buyIntention.table.shippingFee')}</S.TitleText>
                <S.SubText>
                  {t('buyIntention.table.shippingTotalFee')}
                </S.SubText>
              </S.GroupTitleInformation>
            }
            render={(value, _record: BuyIntentionModels.BuyIntentionData) => (
              <S.CenterContent>
                <span>
                  {CurrencyValue(
                    value !== null ? value.productShippingValue : 0,
                  )}
                </span>
                <S.SubTextBody>
                  <span>
                    {CurrencyValue(
                      _record.quantityPacking *
                        _record.quantityUnit *
                        (value !== null ? value.productShippingValue : 0),
                    )}
                  </span>
                </S.SubTextBody>
              </S.CenterContent>
            )}
          />
        )}

        <Table.Column
          width="10%"
          dataIndex="totalPrice"
          key="totalPrice"
          title={
            <S.CenterContent>
              {t('buyIntention.table.totalValue')}
            </S.CenterContent>
          }
          render={(value, _record: BuyIntentionModels.BuyIntentionData) => (
            <S.CenterContent>
              <Monetary symbol value={handleTotalPrice(_record)} />
              {_record.unitPrice > 0 && (
                <S.SubTextBody>
                  {_record.shippingFeeFilials?.productShippingValue ? (
                    <span>
                      {_record.packingSelected.additionalCost === 'S'
                        ? CurrencyValue(
                            _record.packingSelected?.packingCost +
                              _record.unitPrice +
                              _record.shippingFeeFilials?.productShippingValue,
                          )
                        : CurrencyValue(
                            _record.shippingFeeFilials?.productShippingValue +
                              _record.unitPrice,
                          )}
                    </span>
                  ) : (
                    <span>
                      {_record.packingSelected.additionalCost === 'S'
                        ? CurrencyValue(
                            _record.packingSelected?.packingCost +
                              _record.unitPrice,
                          )
                        : CurrencyValue(_record.unitPrice)}
                    </span>
                  )}
                </S.SubTextBody>
              )}
            </S.CenterContent>
          )}
        />

        <Table.Column
          dataIndex="observationText"
          key="observationText"
          title={
            <S.CenterContent>
              {t('buyIntention.table.observation')}
            </S.CenterContent>
          }
          render={(observation, record: any) => (
            <S.ObsContent>
              <Input
                value={observation}
                style={{
                  width: 100,
                }}
                onDoubleClick={() => openObservation(observation, record)}
                onChange={(e) =>
                  changeOfferInput(e.target.value, 'observationText', record)
                }
              />
            </S.ObsContent>
          )}
        />

        <Table.Column
          key="action"
          render={(
            _,
            record: BuyIntentionModels.BuyIntentionData,
            _index: number,
          ) => (
            <S.ObsContent>
              {currentStep === 0 ? (
                <Tooltip title="Adicionar produto">
                  <PlusOutlined onClick={() => searchOffers(record)} />
                </Tooltip>
              ) : (
                <DeleteOutlined onClick={() => requestDelete(record)} />
              )}
            </S.ObsContent>
          )}
        />
      </Table>

      <BuyIntentionItemsLoading isLoading={isLoading} />
    </>
  );
}
